import {type PayloadAction, createSlice} from '@reduxjs/toolkit';
import {assert} from 'assert-ts';
import isEqual from 'lodash/isEqual';
import {Concept} from 'types';
import type {ChangeRequestAction, DataValue} from 'schemaDefinition/types';
import type {CodeListMap} from 'services/codelists/types';
import type {RootState} from 'store/types';
import type {
  ChangeRequest,
  EditMode,
  ExpressionV4,
  ManifestationStatus,
  ManifestationV4,
  Metadata,
  MetadataEditState,
  WorkV4,
} from './types';
import {
  addApproval,
  addNewExpression,
  aggregateChangedStatus,
  aggregatePreviewStatus,
  aggregateStatuses,
  assertEntityStatus,
  assertExpression,
  assertManifestation,
  assertMetadata,
  cancelMoveManifestationToNewExpression,
  changeManifestationExpressionId,
  createExpression,
  getMetadataExports,
  getMetadataOrder,
  getMetadataTimestamps,
  incrementMetadataTimestampForId,
  incrementMetadataTimestamps,
  moveManifestationToNewExpression,
  putById,
  removeById,
  removeEditStatus,
  replaceChangeRequest,
  replaceExpression,
  replaceLocalExpression,
  replaceManifestation,
  replaceWork,
  updateEditStatusOnCancelMoveToNewExpression,
  updateEditStatusOnMove,
  updateEditStatusOnMoveToNewExpression,
  updateEntitySavedStatus,
  updateExpressionExportProps,
  updateManifestationExportProps,
  updateWorkExportProps,
} from './functions';
import {
  type MetadataEditAction_CancelMoveManifestationToNewExpression,
  type MetadataEditAction_ChangeRequestEdited,
  type MetadataEditAction_ChangeRequestSaved,
  type MetadataEditAction_ExpressionEdited,
  type MetadataEditAction_ExpressionSaved,
  type MetadataEditAction_ManifestationSaved,
  type MetadataEditAction_ManifestationStatusChanged,
  type MetadataEditAction_ManifestationStatusPreview,
  type MetadataEditAction_MoveManifestationToNewExpression,
  type MetadataEditAction_MoveManifestationToOtherExpression,
  type MetadataEditAction_NewExpressionWithManifestationSaved,
  type MetadataEditAction_WorkSaved,
  MetadataEditDefaultState,
} from './metadataEditActionTypes';
import {
  metadataEditExpressionEditedReducer,
  metadataEditManifestationEditedReducer,
  metadataEditWorkEditedReducer,
} from './reducers';

export const initialState: MetadataEditState = MetadataEditDefaultState;
const metadataEditSlice = createSlice({
  name: 'metadataEdit',
  initialState,
  reducers: {
    // METADATA_RESET
    metadataEditReset() {
      return initialState;
    },
    // METADATAEDIT_SET_SAVING
    metadataEditSetSaving(
      state,
      action: PayloadAction<{
        saving: boolean;
      }>,
    ) {
      state.saving = action.payload.saving;
    },
    // METADATAEDIT_LOADED
    metadataEditLoaded(
      state,
      action: PayloadAction<{
        codelists: CodeListMap;
        metadata: Metadata;
      }>,
    ) {
      state.savedMetadata = action.payload.metadata;
      state.metadata = action.payload.metadata;
      state.timestamps = getMetadataTimestamps(action.payload.metadata);
      state.order = getMetadataOrder(
        action.payload.metadata,
        [],
        action.payload.codelists,
      );
      state.statuses = aggregateStatuses(action.payload.metadata);
      state.exports = getMetadataExports(action.payload.metadata);
    },
    metadataEditCancelled(
      state,
      action: PayloadAction<{
        codelists: CodeListMap;
      }>,
    ) {
      const savedMetadata = assertMetadata(state.savedMetadata);

      state.savedMetadata = savedMetadata;
      state.metadata = savedMetadata;
      state.approvedChanges = initialState.approvedChanges;
      state.timestamps = incrementMetadataTimestamps(state.timestamps);
      state.newExpressionIds = initialState.newExpressionIds;
      state.order = getMetadataOrder(
        savedMetadata,
        [],
        action.payload.codelists,
      );
      state.statuses = aggregateStatuses(savedMetadata);
      state.editStatuses = initialState.editStatuses;
      state.editModes = initialState.editModes;
      state.exports = getMetadataExports(savedMetadata);
    },
    metadataEditSaved(
      state,
      action: PayloadAction<{
        codelists: CodeListMap;
      }>,
    ) {
      const savedMetadata = assertMetadata(state.savedMetadata);

      state.savedMetadata = savedMetadata;
      state.metadata = savedMetadata;
      state.approvedChanges = initialState.approvedChanges;
      state.timestamps = incrementMetadataTimestamps(state.timestamps);
      state.newExpressionIds = initialState.newExpressionIds;
      state.order = getMetadataOrder(
        savedMetadata,
        [],
        action.payload.codelists,
      );
      state.statuses = aggregateStatuses(savedMetadata);
      state.editStatuses = initialState.editStatuses;
      state.editModes = initialState.editModes;
      state.exports = getMetadataExports(savedMetadata);
    },
    metadataEditWorkEdited: metadataEditWorkEditedReducer,
    // METADATA_WORK_SAVED
    metadataEditWorkSaved(state, action: PayloadAction<WorkV4>) {
      const originalAction: MetadataEditAction_WorkSaved = {
        type: 'METADATAEDIT_WORK_SAVED',
        payload: action.payload,
      };
      const savedMetadata = assertMetadata(
        state.savedMetadata,
        originalAction,
        'saved',
      );
      const metadata = assertMetadata(state.metadata, originalAction, 'edit');
      const newWork = action.payload;

      state.savedMetadata = replaceWork(newWork, savedMetadata);
      state.metadata = replaceWork(newWork, metadata);
      state.timestamps = incrementMetadataTimestampForId(
        newWork.id,
        state.timestamps,
      );
      state.statuses = updateEntitySavedStatus(
        newWork.id,
        state.statuses,
        originalAction,
      );
      state.editStatuses = removeEditStatus(newWork.id, state.editStatuses);
      state.editModes = removeById(newWork.id, state.editModes);
      state.exports = updateWorkExportProps(newWork, state.exports);
    },
    metadataEditExpressionEdited: metadataEditExpressionEditedReducer,
    metadataEditNewExpressionEdited(
      state,
      action: PayloadAction<ExpressionV4>,
    ) {
      const originalAction: MetadataEditAction_ExpressionEdited = {
        type: 'METADATAEDIT_NEW_EXPRESSION_EDITED',
        payload: action.payload,
      };

      const currentMetadata = assertMetadata(
        state.metadata,
        originalAction,
        'edit',
      );
      const currentExpression = assertExpression(
        action.payload.id,
        state.metadata,
        originalAction,
        'edit',
      );

      const changedExpression = {...currentExpression, ...action.payload};
      const changedMetadata = replaceExpression(
        changedExpression,
        currentMetadata,
      );

      if (!isEqual(changedMetadata, currentMetadata)) {
        state.metadata = changedMetadata;
        state.exports = updateExpressionExportProps(
          changedExpression,
          state.exports,
        );
      }
    },
    // METADATA_EXPRESSION_SAVED
    metadataEditExpressionSaved(state, action: PayloadAction<ExpressionV4>) {
      const originalAction: MetadataEditAction_ExpressionSaved = {
        type: 'METADATAEDIT_EXPRESSION_SAVED',
        payload: action.payload,
      };
      const savedMetadata = assertMetadata(
        state.savedMetadata,
        originalAction,
        'saved',
      );
      const metadata = assertMetadata(state.metadata, originalAction, 'edit');
      const newExpression = action.payload;

      state.savedMetadata = replaceExpression(newExpression, savedMetadata);
      state.metadata = replaceExpression(newExpression, metadata);
      state.timestamps = incrementMetadataTimestampForId(
        newExpression.id,
        state.timestamps,
      );
      state.statuses = updateEntitySavedStatus(
        newExpression.id,
        state.statuses,
        originalAction,
      );
      state.editStatuses = removeEditStatus(
        newExpression.id,
        state.editStatuses,
      );
      state.editModes = removeById(newExpression.id, state.editModes);
      state.exports = updateExpressionExportProps(newExpression, state.exports);
    },
    // METADATA_NEW_EXPRESSION_WITH_MANIFESTATION_SAVED
    metadataEditNewExpressionWithManifestationSaved(
      state,
      action: PayloadAction<{
        localExpressionId: string;
        expression: ExpressionV4;
        manifestation: ManifestationV4;
        codelists: CodeListMap;
      }>,
    ) {
      const originalAction: MetadataEditAction_NewExpressionWithManifestationSaved =
        {
          type: 'METADATAEDIT_NEW_EXPRESSION_WITH_MANIFESTATION_SAVED',
          payload: action.payload,
        };

      const savedMetadata = assertMetadata(
        state.savedMetadata,
        originalAction,
        'saved',
      );
      const metadata = assertMetadata(state.metadata, originalAction, 'edit');
      const {
        localExpressionId,
        expression: savedExpression,
        manifestation: savedManifestation,
      } = action.payload;

      const newSavedMetadata1 = replaceManifestation(
        savedManifestation,
        savedMetadata,
      );
      const newSavedMetadata2 = addNewExpression(
        savedExpression,
        newSavedMetadata1,
      );

      const newMetadata1 = replaceManifestation(savedManifestation, metadata);
      const newMetadata2 = replaceLocalExpression(
        localExpressionId,
        savedExpression,
        newMetadata1,
      );

      const newTimestamps1 = incrementMetadataTimestampForId(
        savedExpression.id,
        state.timestamps,
      );
      const newTimestamps2 = incrementMetadataTimestampForId(
        savedManifestation.id,
        newTimestamps1,
      );

      const newNewExpressionIds = state.newExpressionIds.filter(
        id => id !== localExpressionId,
      );

      const newEditStatuses1 = removeEditStatus(
        savedManifestation.id,
        state.editStatuses,
      );
      const newEditStatuses2 = removeEditStatus(
        localExpressionId,
        newEditStatuses1,
      );

      const newEditModes1 = removeById(savedManifestation.id, state.editModes);
      const newEditModes2 = removeById(localExpressionId, newEditModes1);

      state.savedMetadata = newSavedMetadata2;
      state.metadata = newMetadata2;
      state.timestamps = newTimestamps2;
      state.newExpressionIds = newNewExpressionIds;
      // Recalcualte order, statuses and exports - assuming all data have been saved
      state.order = getMetadataOrder(
        newMetadata2,
        newNewExpressionIds,
        action.payload.codelists,
      );
      state.statuses = aggregateStatuses(newMetadata2);
      state.editStatuses = newEditStatuses2;
      state.editModes = newEditModes2;
      state.exports = getMetadataExports(newMetadata2);
    },
    metadataEditChangeRequestEdited(
      state,
      action: PayloadAction<ChangeRequest>,
    ) {
      const originalAction: MetadataEditAction_ChangeRequestEdited = {
        type: 'METADATAEDIT_CHANGEREQUEST_EDITED',
        payload: action.payload,
      };

      const metadata = assertMetadata(state.metadata, originalAction, 'edit');
      state.metadata = replaceChangeRequest(action.payload, metadata);
    },
    metadataEditManifestationEdited: metadataEditManifestationEditedReducer,
    //METADATA_MANIFESTATION_SAVED
    metadataEditManifestationSaved(
      state,
      action: PayloadAction<{
        manifestation: ManifestationV4;
        work: WorkV4;
      }>,
    ) {
      const originalAction: MetadataEditAction_ManifestationSaved = {
        type: 'METADATAEDIT_MANIFESTATION_SAVED',
        payload: action.payload,
      };

      const savedMetadata = assertMetadata(
        state.savedMetadata,
        originalAction,
        'saved',
      );
      const metadata = assertMetadata(state.metadata, originalAction, 'edit');
      const newManifestation = action.payload.manifestation;
      const newWork = action.payload.work;

      state.savedMetadata = replaceManifestation(
        newManifestation,
        savedMetadata,
      );
      state.metadata = replaceWork(
        newWork,
        replaceManifestation(newManifestation, metadata),
      );
      state.timestamps = incrementMetadataTimestampForId(
        newManifestation.id,
        state.timestamps,
      );
      state.statuses = updateEntitySavedStatus(
        newManifestation.id,
        state.statuses,
        originalAction,
      );
      state.editStatuses = removeEditStatus(
        newManifestation.id,
        state.editStatuses,
      );
      state.editModes = removeById(newManifestation.id, state.editModes);
      state.exports = updateManifestationExportProps(
        newManifestation,
        state.exports,
      );
    },
    // METADATAEDIT_APPROVE_CHANGEREQUESTITEM
    metadataEditApproveChangeRequestItem(
      state,
      action: PayloadAction<{
        changeRequestId: string;
        /** Root path of value, including 'work', 'expression' or 'manifestation' */
        rootValuePath: string;
        itemAction: ChangeRequestAction;
        /**
         * Use given value to set value at valuePath, independent action specified
         * If not provided, change request value at valuePath will be used
         */
        newValue?: DataValue;

        /**
         * "Path" to review status, default same as valuePath. Should be provided when
         * value comparison doesn't match specific values in original/change request data,
         * e.g. when comparing thesaurus terms grouped by category
         */
        statusPath?: string;

        codelists: CodeListMap;
      }>,
    ) {
      const {changeRequestId, rootValuePath, itemAction, newValue, statusPath} =
        action.payload;

      const {approvals, updatedEntity, entityType} = addApproval(
        changeRequestId,
        rootValuePath,
        itemAction,
        newValue,
        statusPath,
        state.approvedChanges,
        assert(
          state.metadata,
          'METADATAEDIT_APPROVE_CHANGEREQUESTITEM: metadata expected',
        ),
      );

      state.approvedChanges = approvals;

      switch (entityType) {
        case Concept.work:
          metadataEditWorkEditedReducer(state, {
            type: action.type,
            payload: updatedEntity as WorkV4,
          });
          break;
        case Concept.expression:
          metadataEditExpressionEditedReducer(state, {
            type: action.type,
            payload: updatedEntity as ExpressionV4,
          });
          break;
        case Concept.manifestation:
          metadataEditManifestationEditedReducer(state, {
            type: action.type,
            payload: {
              manifestation: updatedEntity as ManifestationV4,
              codelists: action.payload.codelists,
            },
          });
          break;
        default: {
          assert(
            false,
            'METADATAEDIT_APPROVE_CHANGEREQUESTITEM: unknown entity type',
          );
        }
      }
    },
    metadataEditCloseChangeRequest(
      state,
      action: PayloadAction<{
        changeRequestId: string;
      }>,
    ) {
      const {changeRequestId} = action.payload;

      if (state.metadata) {
        const changeRequest = state.metadata.changeRequests?.find(
          cr => cr.id === changeRequestId,
        );

        if (changeRequest) {
          changeRequest.status = 'COMPLETED';

          if (changeRequest.storage !== 'transient') {
            state.editStatuses[changeRequestId] = {
              ...(state.editStatuses[changeRequestId] ?? {}),
              hasDataChanged: true,
            };
          }

          state.approvedChanges[changeRequestId] = {
            rootPaths: [],
            arraysUpdateHistory: {},
            isCompleted: true,
          };
        }
      }
    },
    // METADATAEDIT_NEW_CHANGEREQUEST_FROM_COPY
    metadataEditNewChangeRequestFromCopy(
      state,
      action: PayloadAction<ChangeRequest>,
    ) {
      if (state.metadata) {
        state.metadata.changeRequests.push(action.payload);
      }
    },
    // METADATAEDIT_MANIFESTATION_STAUTS_PREVIEW
    metadataEditManifestationStatusPreview(
      state,
      action: PayloadAction<{
        id: string;
        status?: ManifestationStatus;
      }>,
    ) {
      const originalAction: MetadataEditAction_ManifestationStatusPreview = {
        type: 'METADATAEDIT_MANIFESTATION_STAUTS_PREVIEW',
        payload: action.payload,
      };

      const metadata = assertMetadata(state.metadata, originalAction, 'edit');
      const currentEntityStatus = assertEntityStatus(
        action.payload.id,
        state,
        originalAction,
      );

      const newPreviewStatus: ManifestationStatus | undefined =
        action.payload.status ===
        (currentEntityStatus?.changedStatus ?? currentEntityStatus.status)
          ? undefined
          : action.payload.status;

      state.statuses = aggregatePreviewStatus(
        newPreviewStatus,
        action.payload.id,
        state.statuses,
        metadata,
      );
    },
    //METADATAEDIT_MANIFESTATION_STATUS_CHANGED
    metadataEditManifestationStatusChanged(
      state,
      action: PayloadAction<{
        id: string;
        status?: ManifestationStatus;
      }>,
    ) {
      const originalAction: MetadataEditAction_ManifestationStatusChanged = {
        type: 'METADATAEDIT_MANIFESTATION_STATUS_CHANGED',
        payload: action.payload,
      };

      const metadata = assertMetadata(state.metadata, originalAction, 'edit');
      const currentEntityStatus = assertEntityStatus(
        action.payload.id,
        state,
        originalAction,
      );

      const newChangedStatus: ManifestationStatus | undefined =
        action.payload.status === currentEntityStatus.status
          ? undefined
          : action.payload.status;

      state.statuses = aggregateChangedStatus(
        newChangedStatus,
        action.payload.id,
        state.statuses,
        metadata,
      );
    },
    metadataEditMoveManifestationToOtherExpression(
      state,
      action: PayloadAction<{
        manifestationId: string;
        expressionId: string;
        codelists: CodeListMap;
      }>,
    ) {
      const originalAction: MetadataEditAction_MoveManifestationToOtherExpression =
        {
          type: 'MOVE_MANIFESTATION_TO_OTHER_EXPRESSION',
          payload: action.payload,
        };

      const metadata = assertMetadata(state.metadata, originalAction, 'edit');
      const {manifestationId, expressionId} = action.payload;

      const newMetadata = changeManifestationExpressionId(
        manifestationId,
        expressionId,
        metadata,
        originalAction,
      );

      state.metadata = newMetadata;
      // Recalcualte order, statuses and exports - assuming all data have been saved
      state.order = getMetadataOrder(
        newMetadata,
        state.newExpressionIds,
        action.payload.codelists,
      );
      state.statuses = aggregateStatuses(newMetadata);
      state.editStatuses = updateEditStatusOnMove(
        manifestationId,
        expressionId,
        state.editStatuses,
        state,
      );
      state.exports = getMetadataExports(newMetadata);
    },
    // MOVE_MANIFESTATION_TO_NEW_EXPRESSION
    metadataEditMoveManifestationToNewExpression(
      state,
      action: PayloadAction<{
        manifestationId: string;
        codelists: CodeListMap;
      }>,
    ) {
      const originalAction: MetadataEditAction_MoveManifestationToNewExpression =
        {
          type: 'MOVE_MANIFESTATION_TO_NEW_EXPRESSION',
          payload: action.payload,
        };

      const metadata = assertMetadata(state.metadata, originalAction, 'edit');
      const {manifestationId} = action.payload;
      const manifestation = assertManifestation(
        manifestationId,
        metadata,
        originalAction,
        'edit',
      );
      const fromExpression = assertExpression(
        manifestation.expressionId,
        metadata,
        originalAction,
        'edit',
      );

      const newExpression = createExpression(fromExpression);

      const newMetadata = moveManifestationToNewExpression(
        manifestationId,
        newExpression,
        metadata,
        originalAction,
      );

      const newNewExpressionIds = [...state.newExpressionIds, newExpression.id];

      state.metadata = newMetadata;
      state.newExpressionIds.push(newExpression.id);

      // Recalcualte order, statuses and exports - assuming all data have been saved
      state.order = getMetadataOrder(
        newMetadata,
        newNewExpressionIds,
        action.payload.codelists,
      );
      state.statuses = aggregateStatuses(newMetadata);
      state.editStatuses = updateEditStatusOnMoveToNewExpression(
        manifestationId,
        newExpression.id,
        state.editStatuses,
      );
      state.exports = getMetadataExports(newMetadata);
    },
    // CANCEL_MOVE_MANIFESTATION_TO_NEW_EXPRESSION
    metadataEditCancelMoveManifestationToNewExpression(
      state,
      action: PayloadAction<{
        manifestationId: string;
        codelists: CodeListMap;
      }>,
    ) {
      const originalAction: MetadataEditAction_CancelMoveManifestationToNewExpression =
        {
          type: 'CANCEL_MOVE_MANIFESTATION_TO_NEW_EXPRESSION',
          payload: action.payload,
        };

      const metadata = assertMetadata(state.metadata, originalAction, 'edit');
      const savedMetadata = assertMetadata(
        state.savedMetadata,
        originalAction,
        'edit',
      );
      const {manifestationId} = action.payload;
      const manifestation = assertManifestation(
        manifestationId,
        metadata,
        originalAction,
        'edit',
      );
      const newExpressionId = manifestation.expressionId;
      const savedManifestation = assertManifestation(
        manifestationId,
        savedMetadata,
        originalAction,
        'saved',
      );

      const newMetadata = cancelMoveManifestationToNewExpression(
        manifestation,
        savedManifestation,
        metadata,
        originalAction,
      );

      const newNewExpressionIds = state.newExpressionIds.filter(
        id => id !== newExpressionId,
      );

      state.metadata = newMetadata;
      state.newExpressionIds = newNewExpressionIds;
      state.order = getMetadataOrder(
        newMetadata,
        newNewExpressionIds,
        action.payload.codelists,
      );

      state.statuses = aggregateStatuses(newMetadata);
      state.editStatuses = updateEditStatusOnCancelMoveToNewExpression(
        manifestationId,
        newExpressionId,
        state.editStatuses,
      );
      state.exports = getMetadataExports(newMetadata);
    },
    // METADATAEDIT_SET_EDITMODE
    metadataEditSetEditMode(
      state,
      action: PayloadAction<{
        id: string;
        key: string;
        mode: EditMode;
      }>,
    ) {
      state.editModes = putById(
        action.payload.id,
        {[action.payload.key]: action.payload.mode},
        {},
        state.editModes,
      );
    },
    // METADATA_CHANGEREQUEST_SAVED
    metadataEditChangeRequestSaved(
      state,
      action: PayloadAction<ChangeRequest>,
    ) {
      const originalAction: MetadataEditAction_ChangeRequestSaved = {
        type: 'METADATAEDIT_CHANGEREQUEST_SAVED',
        payload: action.payload,
      };

      const savedMetadata = assertMetadata(
        state.savedMetadata,
        originalAction,
        'saved',
      );
      const metadata = assertMetadata(state.metadata, originalAction, 'edit');
      const newCR = action.payload;

      state.savedMetadata = replaceChangeRequest(newCR, savedMetadata);
      state.metadata = replaceChangeRequest(newCR, metadata);
      state.approvedChanges = removeById(newCR.id, state.approvedChanges);
      state.timestamps = incrementMetadataTimestampForId(
        newCR.id,
        state.timestamps,
      );
      state.editStatuses = removeEditStatus(newCR.id, state.editStatuses);
      state.editModes = removeById(newCR.id, state.editModes);
    },
  },
});

export const {
  metadataEditReset,
  metadataEditSetSaving,
  metadataEditLoaded,
  metadataEditCancelled,
  metadataEditSaved,
  metadataEditWorkEdited,
  metadataEditWorkSaved,
  metadataEditExpressionEdited,
  metadataEditNewExpressionEdited,
  metadataEditExpressionSaved,
  metadataEditNewExpressionWithManifestationSaved,
  metadataEditChangeRequestEdited,
  metadataEditManifestationEdited,
  metadataEditManifestationSaved,
  metadataEditApproveChangeRequestItem,
  metadataEditCloseChangeRequest,
  metadataEditNewChangeRequestFromCopy,
  metadataEditManifestationStatusPreview,
  metadataEditManifestationStatusChanged,
  metadataEditMoveManifestationToOtherExpression,
  metadataEditMoveManifestationToNewExpression,
  metadataEditCancelMoveManifestationToNewExpression,
  metadataEditSetEditMode,
  metadataEditChangeRequestSaved,
} = metadataEditSlice.actions;

export const selectMetadataEdit = (state: RootState) => state.metadataEdit;

// ------ metadataEdit.savedMetadata ------
export const selectMetadataEditSavedMetadataSelector = (state: RootState) =>
  state.metadataEdit.savedMetadata;

export const selectMetadataEditSavedWorkSelector = (state: RootState) =>
  state.metadataEdit.savedMetadata?.work;

export const selectMetadataEditSavedMetadataExpressionById =
  (expressionId: string) => (state: RootState) =>
    state.metadataEdit.savedMetadata?.expressions.find(
      expr => expr.id === expressionId,
    );

export const selectMetadataEditSavedMetadataManifestationById =
  (manifestationId: string) => (state: RootState) =>
    state.metadataEdit.savedMetadata?.manifestations.find(
      man => man.id === manifestationId,
    );

export const selectMetadataEditExportsExpressions = (state: RootState) =>
  state.metadataEdit.exports.expressions;

export const selectMetadataEditExportsExpressionById =
  (expressionId: string) => (state: RootState) =>
    state.metadataEdit.exports.expressions[expressionId];

export const selectMetadataEditExportsManifestations = (state: RootState) =>
  state.metadataEdit.exports.manifestations;

export const selectMetadataEditExportsManifestationById =
  (manifestationId: string) => (state: RootState) =>
    state.metadataEdit.exports.manifestations[manifestationId];

export const selectMetadataEditMetadataHasOpenWorkLevelChangeRequests = (
  state: RootState,
) => {
  return !!state.metadataEdit.metadata?.changeRequests.some(
    cr => cr.level === 'work' && cr.status !== 'COMPLETED',
  );
};

// ------ metadataEdit.metadata ------
export const selectMetadataEditEditedWorkSelector = (state: RootState) =>
  state.metadataEdit.metadata?.work;

export const selectMetadataEditMetadataExpressions = (state: RootState) =>
  state.metadataEdit.metadata?.expressions;

export const selectMetadataEditMetadataExpressionById =
  (expressionId: string) => (state: RootState) =>
    (state.metadataEdit.metadata?.expressions ?? []).find(
      m => m.id === expressionId,
    ) ?? undefined;

export const selectMetadataEditMetadataManifestationById =
  (manifestationId: string) => (state: RootState) =>
    (state.metadataEdit.metadata?.manifestations ?? []).find(
      m => m.id === manifestationId,
    ) ?? undefined;

// ------ other ------
export const selectMetadataEditStatuses = (state: RootState) =>
  state.metadataEdit.statuses;

export const selectMetadataEditEditStatuses = (state: RootState) =>
  state.metadataEdit.editStatuses;

export const selectMetadataEditOrder = (state: RootState) =>
  state.metadataEdit.order;

export const selectMetadataEditExportsWork = (state: RootState) =>
  state.metadataEdit.exports.work;

export const selectMetadataEditApprovedChangesById =
  (id: string) => (state: RootState) =>
    state.metadataEdit.approvedChanges[id];

export const selectMetadataEditEditModes = (state: RootState) =>
  state.metadataEdit.editModes;

export const selectMetadataEditNewExpressionIds = (state: RootState) =>
  state.metadataEdit.newExpressionIds;

export const selectMetadataEditSaving = (state: RootState) =>
  state.metadataEdit.saving;

export default metadataEditSlice;
